// ── Small shared UI components for ScopeMigration ──────────────────────────
import React from 'react';
import { Check } from 'lucide-react';
import { ENVIRONMENTS } from '@/config/systemConfig';

// ── StatBadge ────────────────────────────────────────────────────────────────

export function StatBadge({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className={
        'rounded border px-2 py-0.5 ' +
        (accent ? 'border-emerald-800/60' : 'border-neutral-800')
      }
    >
      <span className="text-[9px] text-neutral-500">{label} </span>
      <span className="text-[11px] text-neutral-200 tabular-nums font-medium">
        {value}
      </span>
    </div>
  );
}

// ── EnvDropdown (driven by system_config.json) ───────────────────────────────

export function EnvDropdown({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-[10px] text-neutral-500 shrink-0 uppercase tracking-wider">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1 rounded pac-glass-input text-xs text-neutral-200 outline-none focus:border-neutral-500 transition-colors cursor-pointer appearance-none pr-6"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23737373' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 6px center',
        }}
      >
        {ENVIRONMENTS.map((env) => (
          <option key={env.key} value={env.key}>
            {env.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ── StepPill for the export stepper ──────────────────────────────────────────

export function StepPill({
  step,
  label,
  active,
  done,
}: {
  step: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <div
      className={
        'flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] leading-none border transition-colors ' +
        (active
          ? 'border-sky-600 bg-sky-950/40 text-sky-300'
          : done
            ? 'border-emerald-700 bg-emerald-950/30 text-emerald-400'
            : 'border-neutral-800 pac-glass-subtle text-neutral-600')
      }
    >
      {done ? (
        <Check className="w-2.5 h-2.5" />
      ) : (
        <span className="tabular-nums">{step}</span>
      )}
      <span>{label}</span>
    </div>
  );
}
